import { useState, useEffect } from 'react';
import { shouldShowMaintenance } from '../utils/maintenance';

/**
 * Hook to manage user role and maintenance mode bypass
 * This hook can be extended to integrate with your authentication system
 */
export const useUserRole = () => {
  const [userRole, setUserRole] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // This is where you would typically:
    // 1. Check localStorage/sessionStorage for user data
    // 2. Make API call to get current user role
    // 3. Use your authentication context (Auth0, Firebase, etc.)
    
    const initializeUserRole = () => {
      try {
        // Example: Get user data from localStorage
        const userData = localStorage.getItem('userData');
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUserRole(parsedUser.role || parsedUser.userType);
          setUserEmail(parsedUser.email || parsedUser.emailAddress);
        }
      } catch (error) {
        //console.warn('Error reading user role:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Listen for storage changes (e.g., after login)
    const handleStorageChange = (e) => {
      if (e.key === 'userData') {
        initializeUserRole();
      }
    };

    // Listen for custom login event
    const handleLoginEvent = () => {
      initializeUserRole();
    };

    const handleLogoutEvent = () => {
      setUserRole(null);
      setUserEmail(null);
    };

    const handleLogoutStorage = (e) => {
      if (e.key === 'logout') {
        handleLogoutEvent();
      }
    };

    initializeUserRole();

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('storage', handleLogoutStorage);
    window.addEventListener('userLoggedIn', handleLoginEvent);
    window.addEventListener('userLoggedOut', handleLogoutEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storage', handleLogoutStorage);
      window.removeEventListener('userLoggedIn', handleLoginEvent);
      window.removeEventListener('userLoggedOut', handleLogoutEvent);
    };
  }, []);

  /**
   * Check if current user can bypass maintenance mode
   */
  const canBypassMaintenance = () => {
    return !shouldShowMaintenance(userRole, userEmail);
  };

  /**
   * Update user role (call this after login/role change)
   */
  const updateUserRole = (role) => {
    setUserRole(role);
  };

  /**
   * Update user email (call this after login/role change)
   */
  const updateUserEmail = (email) => {
    setUserEmail(email);
  };

  /**
   * Clear user role (call this after logout)
   */
  const clearUserRole = () => {
    setUserRole(null);
    setUserEmail(null);
  };

  return {
    userRole,
    userEmail,
    isLoading,
    canBypassMaintenance,
    updateUserRole,
    updateUserEmail,
    clearUserRole
  };
};

export default useUserRole;
